import { TimelineItemData } from "../../TimelineItem/types";

/**
 * Counts of pending changes in timeline items
 */
export interface ChangesCounts {
  added: number;
  edited: number;
  deleted: number;
}

/**
 * Computes counts of added and deleted items from timeline item flags.
 *
 * Note: "edited" count is provided externally via localDraftsCount prop since
 * local edits create drafts in the background that aren't reflected in item data.
 *
 * Counting logic:
 * - added: items with `markedForCreation === true`
 * - deleted: items with `markedForDeletion === true`
 * - edited: always 0 (provided externally)
 *
 * @param items - Timeline items to analyze
 * @returns Object with counts for added and deleted items (edited is always 0)
 */
export function computeChangesCounts<T extends string>(
  items: TimelineItemData<T>[],
): ChangesCounts {
  let added = 0;
  let deleted = 0;

  for (const item of items) {
    if (item.markedForCreation) {
      added++;
    } else if (item.markedForDeletion) {
      deleted++;
    }
  }

  return { added, edited: 0, deleted };
}

/**
 * Formats the changes counts into a human-readable summary string.
 * Only includes categories with count > 0.
 *
 * @param counts - The computed change counts
 * @returns Formatted string like "2 added, 1 edited, 3 deleted" or empty string if no changes
 */
export function formatChangesSummary(counts: ChangesCounts): string {
  const parts: string[] = [];

  if (counts.added > 0) {
    parts.push(`${counts.added} added`);
  }
  if (counts.edited > 0) {
    parts.push(`${counts.edited} edited`);
  }
  if (counts.deleted > 0) {
    parts.push(`${counts.deleted} deleted`);
  }

  return parts.join(", ");
}
